import React, { useState, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDriverDetailsById, updateServiceComprehensive, getServicePhotos, cancelService } from '../../services/addService.service';
import { dealerService } from '../../services/dealer.service';
import { formatDateTime, formatDateTimeForInput } from '../../utils/dateUtils';
import { calculatePickupToDropDistance, isGoogleMapsLoaded } from '../../utils/distanceUtils';
import AddressAutocomplete from './AddressAutocomplete';
import VendorLiveTracking from '../VendorLiveTracking';
import CustomerPaymentPanel from '../payments/CustomerPaymentPanel';
import OtpEscalationPanel from '../services/OtpEscalationPanel';
import {
  displayActualTime,
  displayEstimatedTime,
} from '../../utils/serviceMetricsUtils';
import { formatMoney } from '../../utils/formatMoney';
import { useDialog } from '../../contexts/DialogContext';
import { runInLane } from '../../utils/requestLane';

const ServiceDetailsSegment = ({ service, detailedServiceData, caseObj, onServiceUpdated, onPaymentUpdated, serviceTypes, loadingDetailedService }) => {
  const navigate = useNavigate();
  const { toast } = useDialog();
  const [loadingDriver, setLoadingDriver] = useState(false);
  const [driverDetails, setDriverDetails] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Photos state
  const [servicePhotos, setServicePhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [photosError, setPhotosError] = useState('');

  // Editable form data
  const [editData, setEditData] = useState({
    estimated_time_minutes: '',
    actual_time_minutes: '',
    status: '',
    assigned_at: '',
    started_at: '',
    reached_at: '',
    completed_at: '',
    drop_location: {
      address: '',
      city: '',
      state: '',
      latitude: null,
      longitude: null
    }
  });

  // Distance calculation state
  const [calculatedDistance, setCalculatedDistance] = useState(null);
  const [distanceLoading, setDistanceLoading] = useState(false);
  const [distanceError, setDistanceError] = useState('');

  // Vendor tracking modal state
  const [showVendorTrackingModal, setShowVendorTrackingModal] = useState(false);

  // Service status catalog (labels/order/selectability — replaces hardcoded list)
  const [statusCatalog, setStatusCatalog] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await runInLane(() => dealerService.getServiceStatuses({ is_active: true }));
        if (!cancelled) setStatusCatalog(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setStatusCatalog([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Human label for a status code, sourced from the catalog (fallback: prettified code).
  const labelForStatus = (code) => {
    const found = statusCatalog.find(s => s.code === code);
    if (found) return found.label;
    return String(code || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Cancel service modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelOnBehalfOf, setCancelOnBehalfOf] = useState('customer');
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancellationNotes, setCancellationNotes] = useState('');
  // Coded walkaway reason — only used (and required) when cancelling on behalf of the vendor.
  const [cancelReasonCode, setCancelReasonCode] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState('');

  const VENDOR_WITHDRAWAL_REASON_CODES = [
    { value: 'vehicle_unavailable', label: 'Vehicle / equipment not available' },
    { value: 'too_far', label: 'Distance / ETA no longer viable' },
    { value: 'staffing', label: 'No driver / crew available' },
    { value: 'safety', label: 'Unsafe to continue' },
    { value: 'other', label: 'Other' },
  ];

  // Full status list, ordered by the catalog (source of truth — no hardcoding).
  const catalogStatusOptions = () =>
    statusCatalog.map(s => ({ value: s.code, label: s.label }));

  // Get service status options. Prefer the selected service type's transitions
  // (set of statuses referenced), otherwise fall back to the full catalog.
  const getServiceStatuses = () => {
    // Resolve the current service's scheme service type
    const currentServiceTypeId = detailedServiceData?.service?.scheme_service_type_id;
    const currentServiceTypeCode = detailedServiceData?.service?.scheme_service_type?.service_type_code;
    let currentServiceType = (serviceTypes || []).find(st => st.id === currentServiceTypeId);
    if (!currentServiceType && currentServiceTypeCode) {
      currentServiceType = (serviceTypes || []).find(st => st.service_type_code === currentServiceTypeCode);
    }

    const transitions = currentServiceType?.status_transitions;
    if (!transitions || Object.keys(transitions).length === 0) {
      // No transitions defined → full catalog (ordered, labelled).
      return catalogStatusOptions();
    }

    // Build the set of statuses referenced by the transitions map.
    const referenced = new Set();
    Object.keys(transitions).forEach(status => {
      referenced.add(status);
      (transitions[status] || []).forEach(next => referenced.add(next));
    });

    // Order by catalog display_order when available; append any extras.
    const ordered = catalogStatusOptions().filter(o => referenced.has(o.value));
    const known = new Set(ordered.map(o => o.value));
    Array.from(referenced)
      .filter(code => !known.has(code))
      .sort()
      .forEach(code => ordered.push({ value: code, label: labelForStatus(code) }));

    return ordered;
  };

  const serviceStatuses = getServiceStatuses();

  // Recalculate service statuses when service types change
  useEffect(() => {
    // This will trigger a re-render when serviceTypes change
  }, [serviceTypes, detailedServiceData?.service?.scheme_service_type_id]);

  // Initialize edit data when detailed service data changes
  useEffect(() => {
    if (detailedServiceData?.service) {
      setEditData({
        estimated_time_minutes: detailedServiceData.service.estimated_time_minutes || '',
        actual_time_minutes: detailedServiceData.service.actual_time_minutes || '',
        status: detailedServiceData.service.status || '',
        assigned_at: detailedServiceData.service.assigned_at || '',
        started_at: detailedServiceData.service.started_at || '',
        reached_at: detailedServiceData.service.reached_at || '',
        completed_at: detailedServiceData.service.completed_at || '',
        drop_location: {
          address: detailedServiceData.service.drop_location?.address || '',
          city: detailedServiceData.service.drop_location?.city || '',
          state: detailedServiceData.service.drop_location?.state || '',
          latitude: detailedServiceData.service.drop_location?.latitude || null,
          longitude: detailedServiceData.service.drop_location?.longitude || null
        }
      });

      // Reset editing state when data changes
      setIsEditing(false);
      setSaveError('');
      setSaveSuccess(false);
    }
  }, [detailedServiceData]);

  // Fetch service photos when service changes
  useEffect(() => {
    const fetchServicePhotos = async () => {
      if (!detailedServiceData?.service?.id) {
        setServicePhotos([]);
        return;
      }

      setLoadingPhotos(true);
      setPhotosError('');

      try {
        const response = await getServicePhotos(detailedServiceData.service.id);
        setServicePhotos(response.photos || []);
      } catch (error) {
        console.error('Failed to fetch service photos:', error);
        setPhotosError('Failed to load service photos');
        setServicePhotos([]);
      } finally {
        setLoadingPhotos(false);
      }
    };

    fetchServicePhotos();
  }, [detailedServiceData?.service?.id]);

  // Get available status options based on transitions
  const getAvailableStatuses = () => {
    const currentStatus = detailedServiceData?.service?.status;
    if (!currentStatus) return [];

    // Get the current service's scheme service type
    const currentServiceTypeId = detailedServiceData?.service?.scheme_service_type_id;
    const currentServiceTypeCode = detailedServiceData?.service?.scheme_service_type?.service_type_code;

    // Try to find by ID first, then by service type code
    let currentServiceType = serviceTypes.find(st => st.id === currentServiceTypeId);
    if (!currentServiceType && currentServiceTypeCode) {
      currentServiceType = serviceTypes.find(st => st.service_type_code === currentServiceTypeCode);
    }

    if (!currentServiceType || !currentServiceType.status_transitions) {
      // Fallback to original logic if no transitions available
      const currentIndex = serviceStatuses.findIndex(s => s.value === currentStatus);
      if (currentIndex === -1) return [];
      return serviceStatuses.slice(currentIndex + 1);
    }

    // Get all reachable statuses from current status
    const transitions = currentServiceType.status_transitions;
    const reachableStatuses = new Set();

    // Function to recursively find all reachable statuses
    const findReachableStatuses = (status) => {
      const nextStatuses = transitions[status] || [];
      nextStatuses.forEach(nextStatus => {
        if (!reachableStatuses.has(nextStatus)) {
          reachableStatuses.add(nextStatus);
          findReachableStatuses(nextStatus); // Recursively find statuses reachable from nextStatus
        }
      });
    };

    // Start from current status
    findReachableStatuses(currentStatus);

    // Convert to array and create options (labels from catalog)
    let availableStatuses = Array.from(reachableStatuses).sort().map(statusValue => ({
      value: statusValue,
      label: labelForStatus(statusValue)
    }));

    // Filter out completion statuses for towing services without drop location
    if (isTowingService() && !detailedServiceData?.service?.drop_location?.address) {
      availableStatuses = availableStatuses.filter(status =>
        status.value !== 'service_complete' && status.value !== 'service_resolved'
      );
    }

    return availableStatuses;
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle drop location address selection
  const handleDropAddressSelect = async (addressData) => {
    const newDropLocation = {
      address: addressData.address,
      city: addressData.city,
      state: addressData.state,
      latitude: addressData.latitude,
      longitude: addressData.longitude
    };

    setEditData(prev => ({
      ...prev,
      drop_location: newDropLocation
    }));

    // Calculate distance if Google Maps is available
    if (isGoogleMapsLoaded() && detailedServiceData?.service?.pickup_location) {
      setDistanceLoading(true);
      setDistanceError('');

      try {
        const pickupLocation = detailedServiceData.service.pickup_location;
        const distanceResult = await calculatePickupToDropDistance(pickupLocation, newDropLocation);

        setCalculatedDistance(distanceResult);
        console.log('Distance calculated:', distanceResult);
      } catch (error) {
        console.error('Error calculating distance:', error);
        setDistanceError('Failed to calculate distance. Please try again.');
        setCalculatedDistance(null);
      } finally {
        setDistanceLoading(false);
      }
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!detailedServiceData?.service?.id) return;

    // Validate timestamp fields
    const validation = validateTimestampFields();
    if (!validation.isValid) {
      setSaveError(validation.message);
      return;
    }

    setLoadingSave(true);
    setSaveError('');
    setSaveSuccess(false);

    try {
      const updateData = {};

      // Add fields that have changed
      if (editData.estimated_time_minutes !== detailedServiceData.service.estimated_time_minutes) {
        updateData.estimated_time_minutes = parseInt(editData.estimated_time_minutes) || null;
      }

      if (editData.actual_time_minutes !== detailedServiceData.service.actual_time_minutes) {
        updateData.actual_time_minutes = parseInt(editData.actual_time_minutes) || null;
      }

      if (editData.status !== detailedServiceData.service.status) {
        updateData.status = editData.status;
      }

      // Add timestamp fields that have changed
      if (editData.assigned_at !== detailedServiceData.service.assigned_at) {
        updateData.assigned_at = editData.assigned_at ? new Date(editData.assigned_at).toISOString() : null;
      }

      if (editData.started_at !== detailedServiceData.service.started_at) {
        updateData.started_at = editData.started_at ? new Date(editData.started_at).toISOString() : null;
      }

      if (editData.reached_at !== detailedServiceData.service.reached_at) {
        updateData.reached_at = editData.reached_at ? new Date(editData.reached_at).toISOString() : null;
      }

      if (editData.completed_at !== detailedServiceData.service.completed_at) {
        updateData.completed_at = editData.completed_at ? new Date(editData.completed_at).toISOString() : null;
      }

      // Add drop location fields that have changed
      const currentDropLocation = detailedServiceData.service.drop_location || {};
      const newDropLocation = editData.drop_location;

      // If drop location address is empty, set it to null to remove it
      if (!newDropLocation.address || newDropLocation.address.trim() === '') {
        if (currentDropLocation.address) {
          updateData.drop_location = null;
          // Reset distance when removing drop location
          updateData.distance_km = 0;
        }
      } else if (newDropLocation.address !== currentDropLocation.address ||
        newDropLocation.city !== currentDropLocation.city ||
        newDropLocation.state !== currentDropLocation.state ||
        newDropLocation.latitude !== currentDropLocation.latitude ||
        newDropLocation.longitude !== currentDropLocation.longitude) {
        updateData.drop_location = newDropLocation;

        // Add calculated distance if available
        if (calculatedDistance && calculatedDistance.distance > 0) {
          updateData.distance_km = calculatedDistance.distance;
        }
      }

      if (Object.keys(updateData).length === 0) {
        setSaveError('No changes to save');
        setLoadingSave(false);
        return;
      }

      await updateServiceComprehensive(detailedServiceData.service.id, updateData);
      setSaveSuccess(true);
      setIsEditing(false);

      // Immediately refresh data and clear cached details
      try {
        // Clear cached driver details to force refresh (vendor details now come from API)
        setDriverDetails(null);

        // Call the parent callback to refresh case data
        if (onServiceUpdated) {
          onServiceUpdated();
        }

        // Also refresh service photos
        if (detailedServiceData?.service?.id) {
          const photosResponse = await getServicePhotos(detailedServiceData.service.id);
          setServicePhotos(photosResponse.photos || []);
        }
      } catch (refreshError) {
        console.error('Error refreshing data after save:', refreshError);
        // Don't fail the save operation if refresh fails
      }

      // Clear success message after a delay
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);

    } catch (error) {
      console.error('Error updating service:', error);
      setSaveError(error.response?.data?.detail || error.message || 'Failed to update service');
    } finally {
      setLoadingSave(false);
    }
  };

  // Check if status change requires timestamp fields
  const getRequiredTimestampFields = (newStatus) => {
    const timestampMap = {
      'assigned': ['assigned_at'],
      'driver_started': ['assigned_at', 'started_at'],
      'vendor_reached_location': ['assigned_at', 'started_at', 'reached_at'],
      'photos_submitted': ['assigned_at', 'started_at', 'reached_at'],
      'service_complete': ['assigned_at', 'started_at', 'reached_at', 'completed_at'],
      'service_resolved': ['assigned_at', 'started_at', 'reached_at', 'completed_at']
    };
    return timestampMap[newStatus] || [];
  };

  // Validate that required timestamp fields are filled
  const validateTimestampFields = () => {
    const requiredFields = getRequiredTimestampFields(editData.status);
    if (requiredFields.length === 0) return { isValid: true, message: '' };

    const missingFields = [];
    for (const field of requiredFields) {
      const currentValue = editData[field];
      if (!currentValue) {
        missingFields.push(field.replace('_', ' '));
      }
    }

    if (missingFields.length > 0) {
      return {
        isValid: false,
        message: `Please fill in the following required fields before changing status to ${editData.status}: ${missingFields.join(', ')}`
      };
    }

    return { isValid: true, message: '' };
  };

  // Calculate actual time from started_at and completed_at
  const calculateActualTime = () => {
    if (editData.started_at && editData.completed_at) {
      const startTime = new Date(editData.started_at);
      const endTime = new Date(editData.completed_at);
      const diffInMinutes = Math.round((endTime - startTime) / (1000 * 60));
      return diffInMinutes > 0 ? diffInMinutes : 0;
    }
    return null;
  };

  // Auto-fill timestamp when status changes
  const handleStatusChange = (newStatus) => {
    // Check if trying to complete a towing service without drop location
    if ((newStatus === 'service_complete' || newStatus === 'service_resolved') &&
      isTowingService() &&
      !detailedServiceData?.service?.drop_location?.address) {
      toast.error('Cannot complete towing service without a drop location. Please add a drop location first.');
      return;
    }

    const now = new Date().toISOString().slice(0, 16); // Format for datetime-local input

    setEditData(prev => {
      const updated = { ...prev, status: newStatus };

      // Auto-fill timestamps based on status
      switch (newStatus) {
        case 'assigned':
          if (!prev.assigned_at) {
            updated.assigned_at = now;
          }
          break;
        case 'driver_started':
          if (!prev.assigned_at) updated.assigned_at = now;
          if (!prev.started_at) updated.started_at = now;
          break;
        case 'vendor_reached_location':
        case 'photos_submitted':
          if (!prev.assigned_at) updated.assigned_at = now;
          if (!prev.started_at) updated.started_at = now;
          if (!prev.reached_at) updated.reached_at = now;
          break;
        case 'service_complete':
        case 'service_resolved':
          if (!prev.assigned_at) updated.assigned_at = now;
          if (!prev.started_at) updated.started_at = now;
          if (!prev.reached_at) updated.reached_at = now;
          if (!prev.completed_at) updated.completed_at = now;
          // Auto-calculate actual time
          if (updated.started_at && updated.completed_at) {
            const calculatedTime = calculateActualTime();
            if (calculatedTime !== null) {
              updated.actual_time_minutes = calculatedTime.toString();
            }
          }
          break;
        default:
          break;
      }

      return updated;
    });
  };



  // Fetch driver details when service has driver data
  useEffect(() => {
    const fetchDriverDetails = async () => {
      // Check if we have driver data in the new API response structure
      if (detailedServiceData?.driver) {
        setLoadingDriver(true);
        try {
          const driverId = detailedServiceData.driver.id;
          if (driverId) {
            const response = await getDriverDetailsById(driverId);
            setDriverDetails(response);
          } else {
            // Use the driver data directly
            setDriverDetails(detailedServiceData.driver);
          }
        } catch (error) {
          console.error('Failed to fetch driver details:', error);
          // Fallback to using driver data directly
          setDriverDetails(detailedServiceData.driver);
        } finally {
          setLoadingDriver(false);
        }
      } else {
        // Clear driver details if no driver data
        setDriverDetails(null);
      }
    };

    fetchDriverDetails();
  }, [detailedServiceData?.driver, detailedServiceData?.service?.id]);

  const handleAssignVendor = () => {
    if (!service || !caseObj) {
      toast.error('Service and case data are required');
      return;
    }

    navigate(`/vendor-search?serviceId=${service.id}&case_id=${caseObj.case?.id}`);
  };

  const handleTrackVendor = () => {
    if (!service || !service.id) {
      toast.error('Service ID is required for tracking');
      return;
    }

    // Show vendor tracking modal with iframe
    setShowVendorTrackingModal(true);
  };

  const handleCloseVendorTrackingModal = () => {
    setShowVendorTrackingModal(false);
  };

  const handleCancelService = () => {
    // Reset modal state
    setCancelOnBehalfOf('customer');
    setCancellationReason('');
    setCancellationNotes('');
    setCancelReasonCode('');
    setCancelError('');
    setShowCancelModal(true);
  };

  const handleCloseCancelModal = () => {
    setShowCancelModal(false);
    setCancelOnBehalfOf('customer');
    setCancellationReason('');
    setCancellationNotes('');
    setCancelReasonCode('');
    setCancelError('');
  };

  const handleSubmitCancellation = async () => {
    // Validate required fields
    if (!cancelOnBehalfOf) {
      setCancelError('Please select on whose behalf you are cancelling');
      return;
    }

    if (cancelOnBehalfOf === 'vendor' && !cancelReasonCode) {
      setCancelError('Please select a withdrawal reason');
      return;
    }

    if (!cancellationReason.trim()) {
      setCancelError(
        cancelOnBehalfOf === 'vendor' && cancelReasonCode === 'other'
          ? 'Please describe the reason (required for "Other")'
          : 'Cancellation reason is required',
      );
      return;
    }

    if (!cancellationNotes.trim()) {
      setCancelError('Notes are required');
      return;
    }

    setCancelLoading(true);
    setCancelError('');

    try {
      const cancelData = {
        cancelled_by: 'agent',
        on_behalf_of: cancelOnBehalfOf,
        cancellation_reason: cancellationReason.trim(),
        notes: cancellationNotes.trim(),
        ...(cancelOnBehalfOf === 'vendor' ? { reason_code: cancelReasonCode } : {}),
      };

      const response = await cancelService(detailedServiceData.service.id, cancelData);

      // Show success message with details
      const message = response.cancellation_info?.message || 'Service cancelled successfully';
      toast.success(`${message}\nThe page will now refresh.`);

      // Close modal and refresh page
      handleCloseCancelModal();

      // Refresh the page to get updated data
      window.location.reload();

    } catch (error) {
      console.error('Error cancelling service:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to cancel service';
      setCancelError(errorMessage);
    } finally {
      setCancelLoading(false);
    }
  };




  const formatDistance = (distance) => {
    if (!distance) return 'N/A';
    return `${distance} km`;
  };

  const formatAmount = (amount) => {
    if (!amount) return 'N/A';
    return formatMoney(amount);
  };

  // Assign vendor until one is linked (paid customers stay awaiting payment after vendor pick)
  const canAssignVendor = () => {
    const status = detailedServiceData?.service?.status;
    const hasVendor = Boolean(detailedServiceData?.service?.vendor_location_id);
    if (status === 'awaiting_customer_payment' && hasVendor) return false;
    return status === 'pending' || status === 'vendor_not_accepted' || status === 'scheduled' || status === 'awaiting_customer_payment';
  };

  const canTrackVendor = () => {
    const status = detailedServiceData?.service?.status;
    const hasVendor = Boolean(detailedServiceData?.service?.vendor_location_id);
    if (status === 'awaiting_customer_payment' && hasVendor) return true;
    return status && !['pending', 'vendor_not_accepted', 'scheduled', 'awaiting_customer_payment'].includes(status);
  };

  // Check if service can be edited (not complete or cancelled) - allows editing throughout lifecycle
  const canEditService = () => {
    const status = detailedServiceData?.service?.status;
    return status !== 'service_complete' && status !== 'service_cancelled';
  };

  // Check if this is a towing service
  const isTowingService = () => {
    const serviceTypeCode = detailedServiceData?.service?.scheme_service_type?.service_type_code;
    return serviceTypeCode === 'TOW';
  };

  // Check if service can be cancelled (not service_resolved or service_complete)
  const canCancelService = () => {
    const status = detailedServiceData?.service?.status;
    return status && status !== 'service_resolved' && status !== 'service_complete' && status !== 'service_cancelled';
  };

  if (!service) {
    return (
      <div className="bg-gray-50 rounded-lg p-8 border border-gray-200 text-center text-gray-400 text-lg">
        Please select a service from the list above to view details
      </div>
    );
  }

  if (!detailedServiceData || loadingDetailedService) {
    return (
      <div className="bg-gray-50 rounded-lg p-8 border border-gray-200 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-500 mt-2">Loading service details...</p>
      </div>
    );
  }

  // Use detailed service data if available, otherwise fall back to basic service data
  const detailedService = detailedServiceData?.service || service;
  const detailedCase = detailedServiceData?.case || caseObj;
  const customer = detailedServiceData?.customer || detailedCase?.customer;

  const cancelMeta = detailedService?.meta_data || {};
  const storedCancellationReason = String(
    cancelMeta.cancellation_reason || detailedService?.cancellation_reason || '',
  ).trim();
  const cancelledByCustomer =
    cancelMeta.cancelled_by_customer === true ||
    cancelMeta.cancelled_by_customer === 'true' ||
    detailedService?.cancelled_by_customer === true;
  const isServiceCancelled =
    String(detailedService?.status || '')
      .replace(/-/g, '_')
      .toLowerCase() === 'service_cancelled';

  const customerFeedback = detailedServiceData?.feedback || null;
  const feedbackSubmitted = Boolean(detailedServiceData?.feedback_submitted && customerFeedback);
  const overallRating = Number(customerFeedback?.overall_rating || 0);
  const driverRating = Number(customerFeedback?.driver_rating || 0);
  const serviceQualityRating = Number(customerFeedback?.service_quality_rating || 0);
  const responseTimeRating = Number(customerFeedback?.response_time_rating || 0);
  const feedbackText = (customerFeedback?.feedback_text || '').trim();
  const improvementSuggestions = (customerFeedback?.improvement_suggestions || '').trim();

  const renderRatingStars = (rating) => {
    const safeRating = Math.max(0, Math.min(5, Number.isFinite(rating) ? rating : 0));
  return (
      <div className="flex items-center gap-1" aria-label={`Overall rating: ${safeRating} out of 5`}>
        {Array.from({ length: 5 }).map((_, idx) => {
          const filled = idx < safeRating;
          return (
            <svg
              key={idx}
              className={`h-5 w-5 ${filled ? 'text-yellow-400' : 'text-gray-300'}`}
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.97a1 1 0 00.95.69h4.173c.969 0 1.371 1.24.588 1.81l-3.376 2.455a1 1 0 00-.364 1.118l1.287 3.97c.3.921-.755 1.688-1.54 1.118l-3.376-2.455a1 1 0 00-1.176 0l-3.376 2.455c-.784.57-1.838-.197-1.539-1.118l1.286-3.97a1 1 0 00-.364-1.118L2.05 9.397c-.783-.57-.38-1.81.588-1.81h4.173a1 1 0 00.95-.69l1.287-3.97z" />
            </svg>
          );
        })}
          </div>
    );
  };

  return (
    <div className="bg-white mb-5 rounded-lg p-8  border-gray-200 border">
      <OtpEscalationPanel detailedServiceData={detailedServiceData} onResolved={onServiceUpdated} />
      {isServiceCancelled && storedCancellationReason ? (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm font-semibold text-red-800">
            {cancelledByCustomer ? 'Cancelled by customer' : 'Cancelled'}
          </p>
          <p className="text-sm text-red-700 mt-1">{storedCancellationReason}</p>
          {cancelMeta.cancelled_at ? (
            <p className="text-xs text-red-600/80 mt-1">
              {new Date(cancelMeta.cancelled_at).toLocaleString()}
            </p>
          ) : null}
        </div>
      ) : null}
      <div className="flex items-center justify-between mb-6 bg-gray-100 py-2 px-4 rounded-md border-b">
        <div className="text-xl font-semibold">Service Number: {detailedService.service_number || detailedService.service_id || detailedService.id}</div>
        <div className="flex gap-3">
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              disabled={!canEditService()}
              className={`px-4 py-2 text-white rounded transition-colors ${canEditService()
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-gray-400 cursor-not-allowed'
                }`}
            >
              Edit Service
            </button>
          ) : (
            <>
              <button
                onClick={() => {
                  setIsEditing(false);
                  // Clear distance calculation
                  setCalculatedDistance(null);
                  setDistanceError('');
                }}
                className="px-4 py-2 bg-white border text-black rounded hover:bg-gray-100 hover:text-black transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loadingSave}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center"
              >
                {loadingSave ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </>
          )}
          {canAssignVendor() && (
            <button
              onClick={handleAssignVendor}
              className="px-4 py-2 bg-scarlet-600 text-white rounded  transition-colors"
            >
              Assign Vendor
            </button>
          )}
          {canTrackVendor() && (
            <button
              onClick={handleTrackVendor}
              className="px-4 py-2 bg-blue-600 text-white rounded transition-colors"
            >
              Track Vendor
            </button>
          )}
          {canCancelService() && (
            <button
              onClick={handleCancelService}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              Cancel Service
            </button>
          )}
        </div>
      </div>

      {/* Success/Error Messages */}
      {saveSuccess && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-green-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-green-800 font-medium">Service updated successfully!</span>
          </div>
        </div>
      )}

      {saveError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-red-800 font-medium">{saveError}</span>
          </div>
        </div>
      )}

      {/* Status change warning */}
      {isEditing && editData.status !== detailedService.status && getRequiredTimestampFields(editData.status).length > 0 && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-yellow-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-yellow-800 font-medium">
              Changing status to "{editData.status}" requires the following timestamp fields: {getRequiredTimestampFields(editData.status).map(field => field.replace('_', ' ')).join(', ')}. These will be auto-filled with current time if not already set.
            </span>
          </div>
        </div>
      )}

      <div className="space-y-6">
        <CustomerPaymentPanel
          serviceId={detailedService.id}
          caseId={detailedCase?.id || caseObj?.case?.id}
          serviceNumber={detailedService.service_number}
          serviceStatus={detailedService.status}
          customerMobile={customer?.mobile_number || detailedCase?.contactable_number}
          customerName={customer?.name}
          estimatedAmount={
            detailedService.estimated_cost_to_customer ?? detailedService.estimated_cost
          }
          vendorAssigned={Boolean(detailedService.vendor_location_id)}
          isPaidCustomer={Boolean(customer?.is_paid_customer)}
          cancellationCharges={
            (detailedService?.meta_data && detailedService.meta_data.cancellation_charges) || null
          }
          onPaymentUpdated={() => {
            if (typeof onPaymentUpdated === 'function') onPaymentUpdated();
            else if (typeof onServiceUpdated === 'function') onServiceUpdated();
          }}
        />

        {/* Location Details Section */}
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-6 text-black border-b ">Location Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className='mb-6'>
              <label className="block text-sm font-nomal text-black mb-2">Service Type:</label>
              <input
                className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                value={detailedService.scheme_service_type?.service_type_name || detailedService.service_type || 'N/A'}
                disabled
        />
            </div>
            <div className='mb-6'>
              <label className="block text-sm font-nomal text-black mb-2">Service Sub Type:</label>
              <input
                className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                value={detailedService.scheme_service_subtype?.subtype_name || detailedService.service_sub_type || 'N/A'}
                disabled
              />
            </div>
            <div className='mb-6'>
              <label className="block text-sm font-nomal text-black mb-2">Status:</label>
        {isEditing ? (
                <>
                <div className="relative group">
                  <select
                    value={editData.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                      className="drop-black w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={detailedService.status}>
                      {labelForStatus(detailedService.status)} (Current)
                    </option>
                      {getAvailableStatuses().map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>

                    {/* Tooltip for towing services without drop location */}
                    {isTowingService() && !detailedService.drop_location?.address && (
                      <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                        <div className="bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                          <div className="flex items-center">
                            <svg className="h-3 w-3 text-yellow-400 mr-1" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <span>Add drop location to enable completion</span>
                      </div>
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                    </div>
                </div>
                    )}
              </div>
                </>
              ) : (
                <input
                  className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                  value={detailedService.status ? labelForStatus(detailedService.status) : 'N/A'}
                  disabled
                />
              )}
            </div>
            <div className="md:col-span-2 mb-6">
              <label className="block text-sm font-nomal text-black mb-2">Pickup Location:</label>
              <input
                className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                value={detailedService.pickup_location?.address || 'N/A'}
                disabled
              />
            </div>
            <div className='mb-6'>
              <label className="block text-sm font-nomal text-black mb-2">Pickup City:</label>
              <input
                className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                value={detailedService.pickup_location?.city || 'N/A'}
                disabled
              />
            </div>
            <div className='mb-6'>
              <label className="block text-sm font-nomal text-black mb-2">Pickup State:</label>
              <input
                className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                value={detailedService.pickup_location?.state || 'N/A'}
                disabled
              />
            </div>
            {/* Drop Location Section - Always show for towing services */}
            {isTowingService() && (
              <>
                <div className="md:col-span-2 mb-6">
                  <label className="block text-sm font-nomal text-black mb-2">Drop Location:</label>
                  {isEditing ? (
                    <AddressAutocomplete
                      value={editData.drop_location.address}
                      onChange={(value) => setEditData(prev => ({
                          ...prev,
                        drop_location: { ...prev.drop_location, address: value }
                      }))}
                      onAddressSelect={handleDropAddressSelect}
                      placeholder="Search for drop location address in New Zealand..."
                      disabled={!canEditService()}
                    />
                  ) : (
                    <input
                      className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                      value={detailedService.drop_location?.address || 'N/A'}
                      disabled
                    />
                  )}
                  </div>
                <div className='mb-6'>
                  <label className="block text-sm font-nomal text-black mb-2">Drop City:</label>
                      <input
                    className={`w-full px-3 py-2 border rounded ${isEditing
                      ? 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500'
                      : 'bg-gray-50 text-gray-700'
                      }`}
                    value={isEditing ? editData.drop_location.city : (detailedService.drop_location?.city || 'N/A')}
                    disabled={!isEditing}
                    onChange={(e) => isEditing && setEditData(prev => ({
                            ...prev,
                      drop_location: { ...prev.drop_location, city: e.target.value }
                    }))}
                      />
                    </div>
                <div className='mb-6'>
                  <label className="block text-sm font-nomal text-black mb-2">Drop State:</label>
                      <input
                    className={`w-full px-3 py-2 border rounded ${isEditing
                      ? 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500'
                      : 'bg-gray-50 text-gray-700'
                      }`}
                    value={isEditing ? editData.drop_location.state : (detailedService.drop_location?.state || 'N/A')}
                    disabled={!isEditing}
                    onChange={(e) => isEditing && setEditData(prev => ({
                            ...prev,
                      drop_location: { ...prev.drop_location, state: e.target.value }
                    }))}
                      />
                    </div>

                {/* Distance calculation display */}
                {isEditing && (
                  <div className="md:col-span-4 mb-4">
                    {distanceLoading && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center">
                          <svg className="animate-spin h-5 w-5 text-blue-400 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="text-blue-800 font-medium text-sm">Calculating distance...</span>
                  </div>
                    </div>
                    )}

                    {distanceError && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center">
                          <svg className="h-5 w-5 text-red-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          <span className="text-red-800 font-medium text-sm">{distanceError}</span>
                    </div>
                </div>
                    )}

                    {calculatedDistance && !distanceLoading && !distanceError && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <svg className="h-5 w-5 text-green-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-green-800 font-medium text-sm">
                              Distance: {calculatedDistance.distance} km
                            </span>
                          </div>
                          <span className="text-green-600 text-xs">
                            This distance will be saved with the service
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Warning when drop location is null - positioned below all drop location fields */}
                {!detailedService.drop_location?.address && (
                  <div className="md:col-span-4 mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <span className="text-yellow-800 font-medium text-sm whitespace-nowrap">
                        Drop location needs to be filled for service to be complete.
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Time Details Section */}
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-6 text-black border-b">Time Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className='mb-4'>
              <label className="block text-sm font-nomal text-black mb-1">Estimated Time:</label>
              {isEditing && (!detailedService.estimated_time_minutes || detailedService.estimated_time_minutes === 'N/A') ? (
                      <input
                        type="number"
                        value={editData.estimated_time_minutes}
                        onChange={(e) => handleInputChange('estimated_time_minutes', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter minutes"
                      />
                    ) : (
                      <input
                  className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                        value={displayEstimatedTime(detailedService)}
                        disabled
                      />
                    )}
                  </div>
            <div className='mb-4'>
              <label className="block text-sm font-nomal text-black mb-1">Actual Time:</label>
              {isEditing && (!detailedService.actual_time_minutes || detailedService.actual_time_minutes === 'N/A') ? (
                      <input
                        type="number"
                        value={editData.actual_time_minutes}
                        onChange={(e) => handleInputChange('actual_time_minutes', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter minutes"
                      />
                    ) : (
                      <input
                  className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                        value={displayActualTime(detailedService)}
                        disabled
                      />
                    )}
                  </div>
            <div className='mb-4'>
              <label className="block text-sm font-nomal text-black mb-1">Created At:</label>
                    <input
                className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                      value={formatDateTime(detailedService.created_at)}
                      disabled
                    />
                  </div>
            <div className='mb-4'>
              <label className="block text-sm font-nomal text-black mb-1">Assigned At:</label>
              {isEditing && (!detailedService.assigned_at || detailedService.assigned_at === 'N/A') ? (
                      <input
                        type="datetime-local"
                        value={formatDateTimeForInput(editData.assigned_at)}
                        onChange={(e) => handleInputChange('assigned_at', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <input
                  className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                        value={formatDateTime(detailedService.assigned_at)}
                        disabled
                      />
                    )}
                  </div>
            <div className='mb-4'>
              <label className="block text-sm font-medium text-gray-600 mb-1">Started At:</label>
              {isEditing && (!detailedService.started_at || detailedService.started_at === 'N/A') ? (
                      <input
                        type="datetime-local"
                        value={formatDateTimeForInput(editData.started_at)}
                        onChange={(e) => handleInputChange('started_at', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <input
                  className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                        value={formatDateTime(detailedService.started_at)}
                        disabled
                      />
                    )}
                  </div>
            <div className='mb-4'>
              <label className="block text-sm font-nomal text-black mb-1">Reached At:</label>
              {isEditing && (!detailedService.reached_at || detailedService.reached_at === 'N/A') ? (
                      <input
                        type="datetime-local"
                        value={formatDateTimeForInput(editData.reached_at)}
                        onChange={(e) => handleInputChange('reached_at', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <input
                  className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                        value={formatDateTime(detailedService.reached_at)}
                        disabled
                      />
                    )}
                  </div>

            <div className='mb-4'>
              <label className="block text-sm font-nomal text-black mb-1">Completed At:</label>
              {isEditing && (!detailedService.completed_at || detailedService.completed_at === 'N/A') ? (
                      <input
                        type="datetime-local"
                        value={formatDateTimeForInput(editData.completed_at)}
                        onChange={(e) => handleInputChange('completed_at', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <input
                  className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                        value={formatDateTime(detailedService.completed_at)}
                        disabled
                      />
                    )}
                  </div>
            <div className='mb-4'>
              <label className="block text-sm font-nomal text-black mb-1">Scheduled At:</label>
                    <input
                className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                      value={detailedService.scheduled_at ? formatDateTime(detailedService.scheduled_at) : 'Not scheduled'}
                      disabled
                    />
                  </div>
            <div className='mb-4'>
              <label className="block text-sm font-nomal text-black mb-1">Last Updated At:</label>
                    <input
                className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                      value={formatDateTime(detailedService.updated_at)}
                      disabled
                    />
                  </div>
                </div>
              </div>

        {/* Vendor Details Section */}
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-6 text-black border-b">Vendor Details</h3>
          {detailedServiceData?.vendor_location ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-nomal text-black mb-1">Vendor Name:</label>
                <input
                  className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                  value={detailedServiceData.vendor_location.vendor?.vendor_name || 'N/A'}
                  disabled
                />
            </div>
              <div>
                <label className="block text-sm font-nomal text-black mb-1">Vendor Code:</label>
                <input
                  className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                  value={detailedServiceData.vendor_location.vendor?.vendor_code || 'N/A'}
                  disabled
                />
          </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-nomal text-black mb-1">Vendor Address:</label>
                <input
                  className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                  value={detailedServiceData.vendor_location.location_address?.address || 'N/A'}
                  disabled
                />
              </div>
              <div>
                <label className="block text-sm font-nomal text-black mb-1">Contact Number:</label>
                <input
                  className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                  value={detailedServiceData.vendor_location.manager?.mobile || 'N/A'}
                  disabled
                />
              </div>
              <div>
                <label className="block text-sm font-nomal text-black mb-1">Manager Name:</label>
                <input
                  className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                  value={detailedServiceData.vendor_location.manager?.name || 'N/A'}
                  disabled
                />
              </div>
            </div>
          ) : detailedService.vendor_id || detailedService.vendor?.id ? (
            <div className="text-center py-4">
              <p className="text-sm text-gray-500">Failed to load vendor details</p>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-gray-500">No vendor assigned</p>
            </div>
          )}
        </div>

        {/* Driver Details Section */}
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-6 text-black border-b">Driver Details</h3>
          {loadingDriver ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Loading driver details...</p>
            </div>
          ) : driverDetails ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-nomal text-black mb-1">Driver Name:</label>
                <input
                  className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                  value={driverDetails.driver_user?.name || driverDetails.name || 'N/A'}
                  disabled
                />
              </div>
              <div>
                <label className="block text-sm font-nomal text-black mb-1">Phone Number:</label>
                <input
                  className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                  value={driverDetails.driver_user?.mobile_number || driverDetails.mobile || driverDetails.mobile_number || 'N/A'}
                  disabled
                />
              </div>
            </div>
          ) : detailedService.driver_id || detailedService.driver?.id ? (
            <div className="text-center py-4">
              <p className="text-sm text-gray-500">Failed to load driver details</p>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-gray-500">No driver assigned</p>
            </div>
          )}
        </div>

        {/* Distance & Amounts Section */}
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-6 text-black border-b">Distance & Amounts</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Removed service status field */}
            <div>
              <label className="block text-sm font-nomal text-black mb-1">Distance (km):</label>
              <input
                className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                value={formatDistance(detailedService.distance_km)}
                disabled
              />
            </div>
            <div>
              <label className="block text-sm font-nomal text-black mb-1">Estimated Cost:</label>
              <input
                className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700"
                value={formatAmount(detailedService.estimated_cost)}
                disabled
              />
            </div>
          </div>
        </div>

        {/* Service Photos Section */}
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-6 text-black border-b">Service Photos</h3>

          {loadingPhotos ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Loading photos...</p>
            </div>
          ) : photosError ? (
            <div className="text-center py-8">
              <div className="text-red-500 mb-2">
                <svg className="h-8 w-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <p className="text-sm text-gray-600">{photosError}</p>
            </div>
          ) : servicePhotos.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-2">
                <svg className="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-sm text-gray-500">No photos uploaded for this service</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-sm text-gray-600 mb-4">
                Total Photos: {servicePhotos.length}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {servicePhotos.map((photo, index) => (
                  <div key={photo.id || index} className="group relative">
                    <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200 hover:border-blue-300 transition-colors">
                      {photo.download_url ? (
                        <img
                          src={photo.download_url}
                          alt={photo.description || `Photo ${index + 1}`}
                          className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => window.open(photo.download_url, '_blank')}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
        ) : null}

                      {/* Fallback when image fails to load */}
                      <div
                        className="w-full h-full flex items-center justify-center bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors"
                        style={{ display: photo.download_url ? 'none' : 'flex' }}
                        onClick={() => photo.download_url && window.open(photo.download_url, '_blank')}
                      >
                        <div className="text-center">
                          <svg className="h-8 w-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="text-xs text-gray-500">Image unavailable</p>
      </div>
                      </div>
                    </div>

                    {/* Photo info overlay */}
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white p-2 text-xs">
                      <div className="font-medium truncate">{photo.description || `Photo ${index + 1}`}</div>
                      <div className="text-gray-300">
                        {photo.file_size ? `${(photo.file_size / 1024).toFixed(1)} KB` : ''}
                      </div>
                    </div>

                    {/* Open in new tab button */}
                    {photo.download_url && (
                      <button
                        onClick={() => window.open(photo.download_url, '_blank')}
                        className="absolute top-2 right-2 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Open in new tab"
                      >
                        <svg className="h-4 w-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Photo details table */}
              <div className="mt-6">
                <h4 className="text-lg font-semibold text-gray-700 mb-3">Photo Details</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Description</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Type</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">File Size</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Uploaded</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {servicePhotos.map((photo, index) => (
                        <tr key={photo.id || index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-700">
                            {photo.description || `Photo ${index + 1}`}
                          </td>
                          <td className="px-3 py-2 text-gray-600">
                            {photo.photo_type || 'N/A'}
                          </td>
                          <td className="px-3 py-2 text-gray-600">
                            {photo.file_size ? `${(photo.file_size / 1024).toFixed(1)} KB` : 'N/A'}
                          </td>
                          <td className="px-3 py-2 text-gray-600">
                            {photo.uploaded_at ? formatDateTime(photo.uploaded_at) : 'N/A'}
                          </td>
                          <td className="px-3 py-2">
                            {photo.download_url ? (
                              <button
                                onClick={() => window.open(photo.download_url, '_blank')}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                              >
                                <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                                View
                              </button>
                            ) : (
                              <span className="text-gray-400 text-sm">Unavailable</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Customer Feedback Section (bottom, after Photo Details) */}
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-6 text-black border-b">Customer Feedback</h3>

          {feedbackSubmitted ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold text-gray-600">Overall Experience</span>
                  {renderRatingStars(overallRating)}
                  <span className="text-sm font-medium text-gray-700">{overallRating}/5</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {driverRating > 0 ? (
                  <div className="rounded-lg border border-gray-200 p-4">
                    <div className="text-xs font-semibold text-gray-600 mb-2">Driver Behaviour</div>
                    <div className="flex items-center gap-2">
                      {renderRatingStars(driverRating)}
                      <span className="text-sm font-medium text-gray-700">{driverRating}/5</span>
                    </div>
                  </div>
                ) : null}

                {serviceQualityRating > 0 ? (
                  <div className="rounded-lg border border-gray-200 p-4">
                    <div className="text-xs font-semibold text-gray-600 mb-2">Service Quality</div>
                    <div className="flex items-center gap-2">
                      {renderRatingStars(serviceQualityRating)}
                      <span className="text-sm font-medium text-gray-700">{serviceQualityRating}/5</span>
                    </div>
                  </div>
                ) : null}

                {responseTimeRating > 0 ? (
                  <div className="rounded-lg border border-gray-200 p-4">
                    <div className="text-xs font-semibold text-gray-600 mb-2">Response Time</div>
                    <div className="flex items-center gap-2">
                      {renderRatingStars(responseTimeRating)}
                      <span className="text-sm font-medium text-gray-700">{responseTimeRating}/5</span>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="space-y-3">
                  <div>
                    <div className="text-xs font-semibold text-gray-600 mb-1">Feedback</div>
                    <div className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                      {feedbackText ? feedbackText : <span className="text-gray-500">(No written feedback)</span>}
                    </div>
                  </div>

                  {improvementSuggestions ? (
                    <div>
                      <div className="text-xs font-semibold text-gray-600 mb-1">Improvement Suggestions</div>
                      <div className="text-sm text-gray-700 whitespace-pre-wrap break-words">{improvementSuggestions}</div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-sm text-gray-500">No feedback submitted yet.</p>
            </div>
          )}
        </div>
      </div>

      {/* Vendor Tracking Modal */}
      {showVendorTrackingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[95vw] h-[95vh] max-w-7xl max-h-[95vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Vendor Tracking - {service?.service_number}
              </h3>
              <button
                onClick={handleCloseVendorTrackingModal}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            {/* Modal Content - Direct Component */}
            <div className="flex-1 p-0 min-h-0 overflow-hidden">
              <VendorLiveTracking
                serviceId={service?.id}
                isIframe={true}
                polyline={
                  (detailedServiceData?.service?.status === 'service_resolved' || detailedServiceData?.service?.status === 'service_complete')
                    ? (detailedServiceData?.service?.meta_data?.polyline || null)
                    : null
                }
              />
            </div>
          </div>
        </div>
      )}

      {/* Cancel Service Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-xl font-semibold text-gray-900">
                Cancel Service - {service?.service_number}
              </h3>
              <button
                onClick={handleCloseCancelModal}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Error Message */}
                {cancelError && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-red-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <span className="text-red-800 font-medium">{cancelError}</span>
                    </div>
                  </div>
                )}

                {/* On Behalf Of Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    On Behalf Of <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                      style={{ borderColor: cancelOnBehalfOf === 'customer' ? '#3b82f6' : '#e5e7eb' }}>
                      <input
                        type="radio"
                        name="onBehalfOf"
                        value="customer"
                        checked={cancelOnBehalfOf === 'customer'}
                        onChange={(e) => setCancelOnBehalfOf(e.target.value)}
                        className="h-4 w-4 text-blue-600"
                      />
                      <div className="ml-3">
                        <span className="font-medium text-gray-900">Customer</span>
                        <p className="text-sm text-gray-500">
                          Cancelling on behalf of the customer. Cancellation charges may apply.
                        </p>
                      </div>
                    </label>

                    <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                      style={{ borderColor: cancelOnBehalfOf === 'vendor' ? '#3b82f6' : '#e5e7eb' }}>
                      <input
                        type="radio"
                        name="onBehalfOf"
                        value="vendor"
                        checked={cancelOnBehalfOf === 'vendor'}
                        onChange={(e) => setCancelOnBehalfOf(e.target.value)}
                        className="h-4 w-4 text-blue-600"
                      />
                      <div className="ml-3">
                        <span className="font-medium text-gray-900">Vendor</span>
                        <p className="text-sm text-gray-500">
                          Cancelling on behalf of the vendor. Service will be reset to pending status.
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Withdrawal Reason (vendor walkaway only) */}
                {cancelOnBehalfOf === 'vendor' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Withdrawal Reason <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={cancelReasonCode}
                      onChange={(e) => setCancelReasonCode(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select a reason...</option>
                      {VENDOR_WITHDRAWAL_REASON_CODES.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Cancellation Reason */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Cancellation Reason <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={cancellationReason}
                    onChange={(e) => setCancellationReason(e.target.value)}
                    placeholder="Enter the reason for cancellation"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    maxLength={500}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {cancellationReason.length}/500 characters
                  </p>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Additional Notes <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={cancellationNotes}
                    onChange={(e) => setCancellationNotes(e.target.value)}
                    placeholder="Enter any additional notes or details about the cancellation"
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    maxLength={1000}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {cancellationNotes.length}/1000 characters
                  </p>
                </div>

                {/* Warning Message */}
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start">
                    <svg className="h-5 w-5 text-yellow-400 mr-2 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium">Warning</p>
                      <p className="mt-1">
                        {cancelOnBehalfOf === 'customer'
                          ? 'Cancelling on behalf of customer may result in cancellation charges based on the time since service creation.'
                          : 'Cancelling on behalf of vendor will reset the service to pending status and a new vendor search will be initiated.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={handleCloseCancelModal}
                disabled={cancelLoading}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Close
              </button>
              <button
                onClick={handleSubmitCancellation}
                disabled={cancelLoading}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center"
              >
                {cancelLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Cancelling...
                  </>
                ) : (
                  'Confirm Cancellation'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(ServiceDetailsSegment); 